import React, { useState, useEffect, useRef, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Eye, ZoomIn, ZoomOut, X, AlertTriangle, Lightbulb, 
  BookOpen, Maximize2, Minimize2, Search, Plus, Move, RotateCw, RotateCcw, Crosshair, EyeOff
} from 'lucide-react';
import { Alert, AlertDescription } from "@/components/ui/alert";

export interface ZoneDataV2 {
  id: string;
  eye: 'D' | 'G';
  zone_num: number;
  label: string;
  lecture_iridologique: string;
  pistes_accompagnement: string[];
  points_vigilance: string[];
}

// Catégories de systèmes avec couleurs
const SYSTEM_CATEGORIES: Record<string, { color: string; label: string }> = {
  'digestif': { color: 'hsl(142, 76%, 36%)', label: 'Digestif' },
  'respiratoire': { color: 'hsl(199, 89%, 48%)', label: 'Respiratoire' },
  'circulatoire': { color: 'hsl(0, 84%, 60%)', label: 'Circulatoire' },
  'nerveux': { color: 'hsl(271, 81%, 56%)', label: 'Nerveux' },
  'urinaire': { color: 'hsl(45, 93%, 47%)', label: 'Urinaire' },
  'génital': { color: 'hsl(330, 81%, 60%)', label: 'Génital' },
  'endocrinien': { color: 'hsl(25, 95%, 53%)', label: 'Endocrinien' },
  'locomoteur': { color: 'hsl(180, 70%, 45%)', label: 'Locomoteur' },
  'immunitaire': { color: 'hsl(280, 70%, 50%)', label: 'Immunitaire' },
  'sensoriel': { color: 'hsl(210, 70%, 50%)', label: 'Sensoriel' },
};

interface CalibrationState {
  scale: number;
  rotation: number;
  offsetX: number;
  offsetY: number;
}

interface IrisSvgMapV2Props {
  eyeSide: 'droit' | 'gauche';
  /** Persist calibration across tab switches for this iridology session */
  sessionId?: string;
  irisPhotoUrl?: string;
  onAddZone?: (zone: ZoneDataV2) => void;
  isZoneInSelection?: (zoneId: string) => boolean;
  className?: string;
  /** Controlled visibility of the map (SVG + photo overlay) */
  showMap?: boolean;
  onToggleMap?: () => void;
}

export const IrisSvgMapV2: React.FC<IrisSvgMapV2Props> = ({
  eyeSide,
  sessionId,
  irisPhotoUrl,
  onAddZone,
  isZoneInSelection,
  className = '',
  showMap = true,
  onToggleMap,
}) => {
  const [zones, setZones] = useState<ZoneDataV2[]>([]);
  const [svgContent, setSvgContent] = useState<string>('');
  const [selectedZone, setSelectedZone] = useState<ZoneDataV2 | null>(null);
  const [highlightedZoneId, setHighlightedZoneId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  
  const [viewZoom, setViewZoom] = useState(1);
  const [zoneOpacity, setZoneOpacity] = useState(0.6);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showCalibration, setShowCalibration] = useState(false);
  
  const DEFAULT_CALIBRATION: CalibrationState = { scale: 1, rotation: 0, offsetX: 0, offsetY: 0 };

  const readStoredCalibration = (eye: 'droit' | 'gauche') => {
    if (!sessionId) return null;
    try {
      const raw = localStorage.getItem(`iridology:calibration:v2:${sessionId}:${eye}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as {
        calibration?: Partial<CalibrationState>;
        locked?: boolean;
      };
      if (!parsed?.calibration) return null;
      return {
        calibration: {
          ...DEFAULT_CALIBRATION,
          ...parsed.calibration,
        },
        locked: Boolean(parsed.locked),
      };
    } catch {
      return null;
    }
  };

  // Calibration states stored separately for each eye
  const [calibrationDroit, setCalibrationDroit] = useState<CalibrationState>(() => {
    return readStoredCalibration('droit')?.calibration ?? DEFAULT_CALIBRATION;
  });
  const [calibrationGauche, setCalibrationGauche] = useState<CalibrationState>(() => {
    return readStoredCalibration('gauche')?.calibration ?? DEFAULT_CALIBRATION;
  });
  const [isCalibrationLockedDroit, setIsCalibrationLockedDroit] = useState(() => {
    return readStoredCalibration('droit')?.locked ?? false;
  });
  const [isCalibrationLockedGauche, setIsCalibrationLockedGauche] = useState(() => {
    return readStoredCalibration('gauche')?.locked ?? false;
  });
  
  // Get current calibration based on eye side
  const calibration = eyeSide === 'droit' ? calibrationDroit : calibrationGauche;
  const isCalibrationLocked = eyeSide === 'droit' ? isCalibrationLockedDroit : isCalibrationLockedGauche;
  
  const setCalibration = (value: CalibrationState | ((prev: CalibrationState) => CalibrationState)) => {
    if (eyeSide === 'droit') {
      setCalibrationDroit(value);
    } else {
      setCalibrationGauche(value);
    }
  };
  
  const setIsCalibrationLocked = (value: boolean) => {
    if (eyeSide === 'droit') {
      setIsCalibrationLockedDroit(value);
    } else {
      setIsCalibrationLockedGauche(value);
    }
  };
  
  const hydratedSessionRef = useRef<string | null>(sessionId ?? null);

  // If the sessionId becomes available later (new session just saved), hydrate from storage once.
  useEffect(() => {
    if (!sessionId) return;
    if (hydratedSessionRef.current === sessionId) return;
    hydratedSessionRef.current = sessionId;

    const storedD = readStoredCalibration('droit');
    if (storedD) {
      setCalibrationDroit(storedD.calibration);
      setIsCalibrationLockedDroit(storedD.locked);
    }

    const storedG = readStoredCalibration('gauche');
    if (storedG) {
      setCalibrationGauche(storedG.calibration);
      setIsCalibrationLockedGauche(storedG.locked);
    }
  }, [sessionId]);

  // Persist calibration so switching tabs never resets it
  useEffect(() => {
    if (!sessionId) return;
    if (hydratedSessionRef.current !== sessionId) return;
    try {
      localStorage.setItem(
        `iridology:calibration:v2:${sessionId}:droit`,
        JSON.stringify({ calibration: calibrationDroit, locked: isCalibrationLockedDroit })
      );
    } catch {}
  }, [sessionId, calibrationDroit, isCalibrationLockedDroit]);

  useEffect(() => {
    if (!sessionId) return;
    if (hydratedSessionRef.current !== sessionId) return;
    try {
      localStorage.setItem(
        `iridology:calibration:v2:${sessionId}:gauche`,
        JSON.stringify({ calibration: calibrationGauche, locked: isCalibrationLockedGauche })
      );
    } catch {}
  }, [sessionId, calibrationGauche, isCalibrationLockedGauche]);

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
  
  const svgContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load SVG and JSON data
  useEffect(() => {
    const loadData = async () => {
      try {
        const svgPath = eyeSide === 'droit'
          ? '/iridology/oeil_droit_zones_v2.svg'
          : '/iridology/oeil_gauche_zones_v2.svg';
        
        const [svgResponse, zonesResponse] = await Promise.all([
          fetch(svgPath),
          fetch('/iridology/zones_data_v2.json'),
        ]);

        if (svgResponse.ok) {
          let svgText = await svgResponse.text();
          // Make SVG responsive
          svgText = svgText.replace(/width="[^"]*"/i, 'width="100%"');
          svgText = svgText.replace(/height="[^"]*"/i, 'height="100%"');
          if (!/preserveAspectRatio=/i.test(svgText)) {
            svgText = svgText.replace('<svg ', '<svg preserveAspectRatio="xMidYMid meet" ');
          }
          // Sanitize SVG content to prevent XSS
          const sanitizedSvg = DOMPurify.sanitize(svgText, { 
            USE_PROFILES: { svg: true, svgFilters: true },
            ADD_TAGS: ['use'],
            ADD_ATTR: ['xlink:href', 'preserveAspectRatio', 'viewBox']
          });
          setSvgContent(sanitizedSvg);
        }

        if (zonesResponse.ok) {
          const allZones: ZoneDataV2[] = await zonesResponse.json();
          // Filter zones for the current eye
          const eyePrefix = eyeSide === 'droit' ? 'D' : 'G';
          const filteredZones = allZones.filter(z => z.eye === eyePrefix);
          setZones(filteredZones);
        }
      } catch (error) {
        console.error('Error loading iridology data:', error);
      }
    };

    loadData();
    setSelectedZone(null);
    setHighlightedZoneId(null);
    // Close calibration panel when switching eyes (but keep calibration values)
    setShowCalibration(false);
  }, [eyeSide]);

  // Handle SVG zone clicks
  useEffect(() => {
    if (!svgContainerRef.current || !svgContent || zones.length === 0) return;

    const svgElement = svgContainerRef.current.querySelector('svg');
    if (!svgElement) return;

    const handleClick = (event: MouseEvent) => {
      // Don't handle clicks when in calibration mode and dragging
      if (showCalibration && isDragging) return;
      
      const target = event.target as Element | null;
      if (!target) return;

      // Find clicked element with an ID by traversing up the DOM
      let element: Element | null = target;
      while (element && !element.id && element.parentElement) {
        if (element.parentElement.tagName.toLowerCase() === 'svg') break;
        element = element.parentElement;
      }

      const elementId = element?.id;
      if (!elementId) return;

      // Find matching zone in JSON data
      const zone = zones.find(z => z.id === elementId);
      if (zone) {
        setSelectedZone(zone);
        setHighlightedZoneId(elementId);
      }
    };

    svgElement.addEventListener('click', handleClick);
    return () => svgElement.removeEventListener('click', handleClick);
  }, [svgContent, zones, showCalibration, isDragging]);

  // Apply visual styling to SVG zones
  useEffect(() => {
    if (!svgContainerRef.current) return;

    const svgElement = svgContainerRef.current.querySelector('svg');
    if (!svgElement) return;

    // Style all path elements
    const paths = svgElement.querySelectorAll<SVGElement>('path, polygon, ellipse, circle, rect');
    paths.forEach((path) => {
      const pathId = path.id || path.closest('[id]')?.id;
      if (!pathId) return;

      const isHighlighted = pathId === highlightedZoneId;
      const isInSelection = isZoneInSelection?.(pathId);

      if (isHighlighted) {
        path.style.fill = 'hsl(var(--primary))';
        path.style.fillOpacity = '0.5';
        path.style.stroke = 'hsl(var(--primary))';
        path.style.strokeWidth = '3';
      } else if (isInSelection) {
        path.style.fill = 'hsl(var(--primary))';
        path.style.fillOpacity = '0.3';
        path.style.stroke = 'hsl(var(--primary))';
        path.style.strokeWidth = '2';
      } else {
        path.style.fillOpacity = String(zoneOpacity);
        path.style.strokeWidth = '0.5';
      }

      path.style.cursor = showCalibration ? 'move' : 'pointer';
      path.style.transition = 'all 0.2s ease';
    });
  }, [highlightedZoneId, svgContent, zoneOpacity, isZoneInSelection, showCalibration]);

  // Drag handlers for manual calibration (only when not locked) - Mouse AND Touch support
  const handleDragStart = useCallback(
    (clientX: number, clientY: number) => {
      if (!showCalibration || isCalibrationLocked) return;
      setIsDragging(true);
      dragStartRef.current = {
        x: clientX,
        y: clientY,
        offsetX: calibration.offsetX,
        offsetY: calibration.offsetY,
      };
    },
    [showCalibration, isCalibrationLocked, calibration.offsetX, calibration.offsetY]
  );

  const handleDragMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!isDragging || !showCalibration || isCalibrationLocked) return;
      const deltaX = (clientX - dragStartRef.current.x) / viewZoom;
      const deltaY = (clientY - dragStartRef.current.y) / viewZoom;
      setCalibration((prev) => ({
        ...prev,
        offsetX: dragStartRef.current.offsetX + deltaX,
        offsetY: dragStartRef.current.offsetY + deltaY,
      }));
    },
    [isDragging, showCalibration, isCalibrationLocked, viewZoom]
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Mouse event handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      handleDragStart(e.clientX, e.clientY);
    },
    [handleDragStart]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      handleDragMove(e.clientX, e.clientY);
    },
    [handleDragMove]
  );

  const handleMouseUp = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Touch event handlers for mobile/tablet
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!showCalibration || isCalibrationLocked) return;
      if (e.touches.length === 1) {
        e.preventDefault();
        const touch = e.touches[0];
        handleDragStart(touch.clientX, touch.clientY);
      }
    },
    [showCalibration, isCalibrationLocked, handleDragStart]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging || e.touches.length !== 1) return;
      e.preventDefault();
      const touch = e.touches[0];
      handleDragMove(touch.clientX, touch.clientY);
    },
    [isDragging, handleDragMove]
  );

  const handleTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Manual offset adjustment for mobile (buttons)
  const adjustOffset = (axis: 'x' | 'y', delta: number) => {
    setCalibration((prev) => ({
      ...prev,
      [axis === 'x' ? 'offsetX' : 'offsetY']: prev[axis === 'x' ? 'offsetX' : 'offsetY'] + delta,
    }));
  };

  const clearSelection = () => {
    setSelectedZone(null);
    setHighlightedZoneId(null);
  };

  const zoomIn = () => setViewZoom((prev) => Math.min(prev + 0.25, 3));
  const zoomOut = () => setViewZoom((prev) => Math.max(prev - 0.25, 0.5));

  const resetCalibration = () => {
    setCalibration({ scale: 1, rotation: 0, offsetX: 0, offsetY: 0 });
    setIsCalibrationLocked(false);
  };
  
  const saveCalibration = () => {
    setIsCalibrationLocked(true);
    setShowCalibration(false);
  };

  const toggleFilter = (filter: string) => {
    setActiveFilters(prev => 
      prev.includes(filter) 
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  // Get display name for zone
  const getZoneDisplayName = (zone: ZoneDataV2): string => {
    if (zone.label && zone.label.trim()) {
      return zone.label;
    }
    return `Zone ${zone.zone_num}`;
  };

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {/* Controls Bar - Unified layout */}
      <div className="flex flex-col gap-2 p-2 bg-muted/50 rounded-lg">
        {/* Row 1: Eye badge + zones count + toggle map */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-xs font-normal">
              <Eye className="h-3 w-3 mr-1" />
              {eyeSide === 'droit' ? 'Droit' : 'Gauche'}
            </Badge>
            <Badge variant="secondary" className="text-xs">
              {zones.length} zones
            </Badge>
          </div>

          {/* Toggle map button - always visible */}
          {onToggleMap && (
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-7 gap-1"
              onClick={onToggleMap}
              title={showMap ? "Masquer la carte" : "Afficher la carte"}
            >
              {showMap ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              {showMap ? "Masquer carte" : "Afficher carte"}
            </Button>
          )}
        </div>

        {/* Row 2: Search + Zoom + Opacity + Calibration + Fullscreen */}
        <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
          {/* Search */}
          <div className="relative hidden sm:block">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-7 h-8 w-32 lg:w-40 text-xs"
            />
          </div>

          {/* Zoom controls */}
          <div className="flex items-center gap-0.5 sm:gap-1 bg-background border rounded-md px-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={zoomOut}>
              <ZoomOut className="h-3.5 w-3.5" />
            </Button>
            <span className="text-xs w-8 sm:w-12 text-center font-medium">{Math.round(viewZoom * 100)}%</span>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={zoomIn}>
              <ZoomIn className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* Opacity control */}
          <div className="flex items-center gap-2 bg-background border rounded-md px-2 py-1">
            <Eye className="h-3.5 w-3.5 text-muted-foreground" />
            <Slider
              value={[zoneOpacity]}
              onValueChange={([v]) => setZoneOpacity(v)}
              min={0.1}
              max={1}
              step={0.1}
              className="w-16 lg:w-20"
            />
            <span className="text-xs w-6 lg:w-8 text-muted-foreground">{Math.round(zoneOpacity * 100)}%</span>
          </div>

          {/* Calibration toggle */}
          {irisPhotoUrl && (
            <Button
              variant={showCalibration ? "default" : isCalibrationLocked ? "secondary" : "outline"}
              size="sm"
              onClick={() => {
                if (isCalibrationLocked) {
                  setIsCalibrationLocked(false);
                  setShowCalibration(true);
                } else {
                  setShowCalibration(!showCalibration);
                }
              }}
              className="text-xs h-8"
            >
              <Crosshair className="h-3.5 w-3.5 sm:mr-1" />
              <span className="hidden sm:inline">{isCalibrationLocked ? "Recalibrer" : "Calibrer"}</span>
            </Button>
          )}

          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setIsFullscreen(!isFullscreen)}
          >
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Calibration Panel */}
      {showCalibration && irisPhotoUrl && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="pt-4 space-y-4">
            <Alert className="py-2">
              <Move className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Glissez le schéma ou utilisez les boutons pour l'aligner sur la photo.
              </AlertDescription>
            </Alert>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium flex items-center gap-1">
                    <ZoomIn className="h-3.5 w-3.5" /> Échelle
                  </span>
                  <span className="text-xs text-muted-foreground font-mono">{(calibration.scale * 100).toFixed(0)}%</span>
                </div>
                <Slider
                  value={[calibration.scale]}
                  onValueChange={([v]) => setCalibration((prev) => ({ ...prev, scale: v }))}
                  min={0.3}
                  max={2}
                  step={0.02}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium flex items-center gap-1">
                    <RotateCw className="h-3.5 w-3.5" /> Rotation
                  </span>
                  <span className="text-xs text-muted-foreground font-mono">{calibration.rotation}°</span>
                </div>
                <Slider
                  value={[calibration.rotation]}
                  onValueChange={([v]) => setCalibration((prev) => ({ ...prev, rotation: v }))}
                  min={-180}
                  max={180}
                  step={1}
                />
              </div>
            </div>

            {/* Manual position controls for mobile */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Position X</span>
                  <span className="text-xs text-muted-foreground font-mono">{calibration.offsetX.toFixed(0)}px</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => adjustOffset('x', -10)}>
                    -10
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => adjustOffset('x', -2)}>
                    -
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => adjustOffset('x', 2)}>
                    +
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => adjustOffset('x', 10)}>
                    +10
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Position Y</span>
                  <span className="text-xs text-muted-foreground font-mono">{calibration.offsetY.toFixed(0)}px</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => adjustOffset('y', -10)}>
                    -10
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => adjustOffset('y', -2)}>
                    -
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => adjustOffset('y', 2)}>
                    +
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => adjustOffset('y', 10)}>
                    +10
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={resetCalibration}>
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                Réinitialiser
              </Button>
              <Button size="sm" onClick={saveCalibration}>
                Enregistrer
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Category Filters */}
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(SYSTEM_CATEGORIES).map(([key, { color, label }]) => (
          <Badge
            key={key}
            variant={activeFilters.includes(key) ? "default" : "outline"}
            className="cursor-pointer text-xs transition-all hover:scale-105"
            style={{ 
              backgroundColor: activeFilters.includes(key) ? color : 'transparent',
              borderColor: color,
              color: activeFilters.includes(key) ? 'white' : color 
            }}
            onClick={() => toggleFilter(key)}
          >
            {label}
          </Badge>
        ))}
      </div>

      {/* Map Container - Mobile Optimized */}
      <Card className={`${isFullscreen ? 'fixed inset-2 sm:inset-4 z-50 bg-background' : ''}`}>
        <CardContent className="p-1 sm:p-2 relative">
          {/* 
            Outer wrapper: always visible, keeps the photo displayed at all times.
            The photo and SVG overlay are separated so hiding the map never hides the photo.
          */}
          <div
            ref={containerRef}
            className="relative bg-muted/10 rounded-lg overflow-hidden border touch-none"
            style={{
              aspectRatio: isFullscreen ? 'auto' : '1024/1142',
              height: isFullscreen ? 'calc(100vh - 4rem)' : 'auto',
              minHeight: '280px',
              maxHeight: isFullscreen ? 'none' : '500px',
            }}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
          >
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                transform: `scale(${viewZoom})`,
                transformOrigin: 'center center',
              }}
            >
              {/* Iris photo background — always visible regardless of showMap */}
              {irisPhotoUrl && (
                <img
                  src={irisPhotoUrl}
                  alt={`Photo de l'iris (${eyeSide})`}
                  className="absolute inset-0 w-full h-full object-contain"
                  draggable={false}
                />
              )}

              {/* SVG zones overlay — hidden when showMap is false, but kept mounted for clickability */}
              <div
                ref={svgContainerRef}
                className={`absolute inset-0 ${showCalibration ? 'cursor-move' : 'cursor-crosshair'}`}
                style={{
                  opacity: showMap === false ? 0 : (irisPhotoUrl ? zoneOpacity : 1),
                  pointerEvents: showMap === false ? 'none' : 'auto',
                  transform: `translate(${calibration.offsetX}px, ${calibration.offsetY}px) scale(${calibration.scale}) rotate(${calibration.rotation}deg)`,
                  transformOrigin: 'center center',
                  touchAction: showCalibration ? 'none' : 'auto',
                  transition: 'opacity 0.2s ease',
                }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                dangerouslySetInnerHTML={{ __html: svgContent }}
              />
            </div>
          </div>

          {isFullscreen && (
            <Button
              variant="secondary"
              size="sm"
              className="absolute top-4 right-4 z-10"
              onClick={() => setIsFullscreen(false)}
            >
              <Minimize2 className="h-4 w-4 mr-1" />
              Fermer
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Zone Info Panel - Mobile Optimized */}
      {selectedZone && (
        <Card className="animate-in slide-in-from-bottom-4 duration-300 border-primary/30 shadow-lg">
          <CardHeader className="p-3 sm:pb-3 bg-gradient-to-r from-primary/5 to-transparent">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-start sm:items-center gap-2 sm:gap-3">
                <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-primary animate-pulse mt-1 sm:mt-0 shrink-0" />
                <div className="flex flex-col min-w-0">
                  <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                    <Badge variant="secondary" className="text-[10px] sm:text-xs font-mono">
                      {selectedZone.id}
                    </Badge>
                    <Badge variant="outline" className="text-[10px] sm:text-xs">
                      Zone n°{selectedZone.zone_num}
                    </Badge>
                  </div>
                  <h3 className="text-base sm:text-xl font-bold text-primary mt-1 truncate">
                    {getZoneDisplayName(selectedZone)}
                  </h3>
                </div>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                {onAddZone && (
                  <Button
                    variant={isZoneInSelection?.(selectedZone.id) ? "secondary" : "default"}
                    size="sm"
                    onClick={() => onAddZone(selectedZone)}
                    className="font-medium flex-1 sm:flex-none text-xs sm:text-sm"
                  >
                    {isZoneInSelection?.(selectedZone.id) ? (
                      <>✓ Ajoutée</>
                    ) : (
                      <>
                        <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                        <span className="hidden sm:inline">Ajouter au compte-rendu</span>
                        <span className="sm:hidden">Ajouter</span>
                      </>
                    )}
                  </Button>
                )}
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={clearSelection}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:pt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {/* Lecture iridologique */}
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-primary border-b pb-2">
                  <BookOpen className="h-3 w-3 sm:h-4 sm:w-4" />
                  Lecture iridologique
                </div>
                <div className="bg-muted/30 rounded-lg p-2 sm:p-3">
                  <p className="text-xs sm:text-sm text-foreground leading-relaxed">
                    {selectedZone.lecture_iridologique && selectedZone.lecture_iridologique.trim() 
                      ? selectedZone.lecture_iridologique 
                      : <span className="italic text-muted-foreground">Aucune information disponible pour cette zone.</span>
                    }
                  </p>
                </div>
              </div>

              {/* Pistes d'accompagnement */}
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-primary border-b pb-2">
                  <Lightbulb className="h-3 w-3 sm:h-4 sm:w-4" />
                  Pistes d'accompagnement
                </div>
                <div className="bg-muted/30 rounded-lg p-2 sm:p-3">
                  {selectedZone.pistes_accompagnement && selectedZone.pistes_accompagnement.length > 0 ? (
                    <ul className="space-y-1.5 sm:space-y-2">
                      {selectedZone.pistes_accompagnement.map((piste, index) => (
                        <li key={index} className="text-xs sm:text-sm text-foreground flex items-start gap-1.5 sm:gap-2">
                          <span className="text-primary mt-0.5 shrink-0">•</span>
                          <span>{piste}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs sm:text-sm text-muted-foreground italic">Aucune piste d'accompagnement définie.</p>
                  )}
                </div>
              </div>

              {/* Points de vigilance */}
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-destructive border-b pb-2">
                  <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4" />
                  Points de vigilance
                </div>
                <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-2 sm:p-3">
                  {selectedZone.points_vigilance && selectedZone.points_vigilance.length > 0 ? (
                    <ul className="space-y-1.5 sm:space-y-2">
                      {selectedZone.points_vigilance.map((point, index) => (
                        <li key={index} className="text-xs sm:text-sm text-foreground flex items-start gap-1.5 sm:gap-2">
                          <span className="text-destructive mt-0.5 shrink-0">⚠</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs sm:text-sm text-muted-foreground italic">Aucun point de vigilance spécifique.</p>
                  )}
                </div>
                <p className="text-[10px] sm:text-xs text-muted-foreground italic border-t pt-2">
                  ⓘ Lecture iridologique non médicale - ne se substitue pas à un avis médical professionnel.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instruction when no zone selected */}
      {!selectedZone && (
        <Alert className="border-primary/20 bg-primary/5">
          <Eye className="h-4 w-4" />
          <AlertDescription className="text-sm">
            Cliquez sur une zone du schéma pour afficher son <strong>label</strong>, sa <strong>lecture iridologique</strong>, les <strong>pistes d'accompagnement</strong> et les <strong>points de vigilance</strong>.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default IrisSvgMapV2;
